import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Row, Col, Card, Statistic, Table, Tag, Progress } from "antd";
import {
    ExperimentOutlined,
    CalendarOutlined,
    UserOutlined,
    FileTextOutlined,
    BellOutlined,
    MedicineBoxOutlined,
    BankOutlined,
    EnvironmentOutlined,
    AppstoreOutlined,
    FileSearchOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined
} from "@ant-design/icons";
import { oneKlicks } from "../../SDK/oneclick";
import { getuserdata } from "../../services/apiService";

const DiagnosticDashboard = () => {
    const [userdata, setUserdata] = useState({});
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalTests: 0,
        totalAppointments: 0,
        totalPatients: 0,
        completedToday: 0
    });

    // Data states
    const [tests, setTests] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [patients, setPatients] = useState([]);
    const [nearbyHospitals, setNearbyHospitals] = useState([]);
    const [nearbyDiagnostics, setNearbyDiagnostics] = useState([]);

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        try {
            const userData = JSON.parse(localStorage.getItem("userData") || "{}");
            setUserdata(userData);

            const userId = await getuserdata('id');

            // Fetch diagnostic tests
            const testsRes = await oneKlicks.read('', 'diagnostic_tests', { diagnostic_id: userId }, 'id DESC');
            if (testsRes && testsRes.data) {
                setTests(testsRes.data);
            }

            // Fetch appointments
            const appointmentsRes = await oneKlicks.read('', 'diagnostic_appointments', { diagnostic_id: userId }, 'id DESC', '', { page: 1, limit: 5 });
            if (appointmentsRes && appointmentsRes.data) {
                setAppointments(appointmentsRes.data);

                // Calculate completed today
                const today = new Date().toISOString().split('T')[0];
                const completedToday = appointmentsRes.data.filter(apt =>
                    apt.status === 'completed' && apt.appointment_date === today
                ).length;

                setStats(prev => ({
                    ...prev,
                    totalAppointments: appointmentsRes.data.length,
                    completedToday
                }));
            }

            // Fetch patients
            const patientsRes = await oneKlicks.read('', 'diagnostic_patients', { diagnostic_id: userId }, 'id DESC', '', { page: 1, limit: 5 });
            if (patientsRes && patientsRes.data) {
                setPatients(patientsRes.data);
                setStats(prev => ({ ...prev, totalPatients: patientsRes.data.length }));
            }

            // Fetch nearby hospitals
            const hospitalsRes = await oneKlicks.read('', 'users', { user_type: 'hospital' }, 'id DESC', '', { page: 1, limit: 5 });
            if (hospitalsRes && hospitalsRes.data) {
                setNearbyHospitals(hospitalsRes.data);
            }

            // Fetch nearby diagnostics
            const diagnosticsRes = await oneKlicks.read('', 'diagnostics', {}, 'id DESC', '', { page: 1, limit: 5 });
            if (diagnosticsRes && diagnosticsRes.data) {
                setNearbyDiagnostics(diagnosticsRes.data.filter(d => d.id !== userId).slice(0, 4));
            }

            // Update stats
            setStats(prev => ({
                ...prev,
                totalTests: testsRes?.data?.length || 0
            }));

        } catch (error) {
            console.error('Dashboard load error:', error);
        } finally {
            setLoading(false);
        }
    };

    const statusColors = {
        pending: 'orange',
        confirmed: 'blue',
        completed: 'green',
        cancelled: 'red'
    };

    const appointmentColumns = [
        { title: 'Patient', dataIndex: 'patient_name', key: 'patient_name' },
        { title: 'Test', dataIndex: 'test_name', key: 'test_name' },
        { title: 'Date', dataIndex: 'appointment_date', key: 'appointment_date' },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => <Tag color={statusColors[status] || 'default'}>{status || 'Pending'}</Tag>
        },
    ];

    const patientColumns = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        {
            title: 'Gender',
            dataIndex: 'gender',
            key: 'gender',
            render: (gender) => <Tag color={gender === 'Male' ? 'blue' : 'pink'}>{gender}</Tag>
        },
        { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    ];

    const testColumns = [
        { title: 'Test Name', dataIndex: 'test_name', key: 'test_name' },
        { title: 'Category', dataIndex: 'category', key: 'category' },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            render: (price) => `₹${price || 0}`
        },
    ];

    return (
        <div className="subbody" style={{ width: '100%', padding: '2% 4%' }}>
            <h4 className="mb-4 fw-bold">
                <ExperimentOutlined className="me-2 text-primary" />
                Diagnostic Center Dashboard
            </h4>

            {/* Stats Cards - Similar to CardViews style */}
            <Row gutter={[16, 16]} className="mb-4">
                <Col xs={24} sm={12} md={6}>
                    <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <span><em style={{ background: '#1890ff', color: '#fff', padding: '5px 12px', borderRadius: '20px' }}>{stats.totalTests}</em></span>
                        <div style={{ marginTop: '15px' }}>
                            <ExperimentOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
                            <h5 style={{ marginTop: '10px', marginBottom: '5px' }}>Diagnostic Tests</h5>
                            <Link to="/diagnostic/tests" style={{ fontSize: '12px' }}>Manage Tests</Link>
                        </div>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <span><em style={{ background: '#52c41a', color: '#fff', padding: '5px 12px', borderRadius: '20px' }}>{stats.totalAppointments}</em></span>
                        <div style={{ marginTop: '15px' }}>
                            <CalendarOutlined style={{ fontSize: '32px', color: '#52c41a' }} />
                            <h5 style={{ marginTop: '10px', marginBottom: '5px' }}>Appointments</h5>
                            <Link to="/diagnostic/appointments" style={{ fontSize: '12px' }}>View All</Link>
                        </div>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <span><em style={{ background: '#722ed1', color: '#fff', padding: '5px 12px', borderRadius: '20px' }}>{stats.totalPatients}</em></span>
                        <div style={{ marginTop: '15px' }}>
                            <UserOutlined style={{ fontSize: '32px', color: '#722ed1' }} />
                            <h5 style={{ marginTop: '10px', marginBottom: '5px' }}>Patients</h5>
                            <Link to="/diagnostic/patients" style={{ fontSize: '12px' }}>View Patients</Link>
                        </div>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <span><em style={{ background: '#faad14', color: '#fff', padding: '5px 12px', borderRadius: '20px' }}>{stats.completedToday}</em></span>
                        <div style={{ marginTop: '15px' }}>
                            <CheckCircleOutlined style={{ fontSize: '32px', color: '#faad14' }} />
                            <h5 style={{ marginTop: '10px', marginBottom: '5px' }}>Completed Today</h5>
                            <span style={{ fontSize: '12px', color: '#666' }}>Tests done today</span>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Second Row of Cards */}
            <Row gutter={[16, 16]} className="mb-4">
                <Col xs={24} sm={12} md={6}>
                    <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <div style={{ marginTop: '15px' }}>
                            <FileSearchOutlined style={{ fontSize: '32px', color: '#13c2c2' }} />
                            <h5 style={{ marginTop: '10px', marginBottom: '5px' }}>Reports</h5>
                            <Link to="/diagnostic/reports" style={{ fontSize: '12px' }}>View Reports</Link>
                        </div>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <span><em style={{ background: '#13c2c2', color: '#fff', padding: '5px 12px', borderRadius: '20px' }}>{nearbyHospitals.length}</em></span>
                        <div style={{ marginTop: '15px' }}>
                            <MedicineBoxOutlined style={{ fontSize: '32px', color: '#13c2c2' }} />
                            <h5 style={{ marginTop: '10px', marginBottom: '5px' }}>Nearby Hospitals</h5>
                            <span style={{ fontSize: '12px', color: '#666' }}>Partner hospitals</span>
                        </div>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <span><em style={{ background: '#eb2f96', color: '#fff', padding: '5px 12px', borderRadius: '20px' }}>{nearbyDiagnostics.length}</em></span>
                        <div style={{ marginTop: '15px' }}>
                            <AppstoreOutlined style={{ fontSize: '32px', color: '#eb2f96' }} />
                            <h5 style={{ marginTop: '10px', marginBottom: '5px' }}>Other Centers</h5>
                            <span style={{ fontSize: '12px', color: '#666' }}>Nearby diagnostics</span>
                        </div>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <div style={{ marginTop: '15px' }}>
                            <BellOutlined style={{ fontSize: '32px', color: '#f5222d' }} />
                            <h5 style={{ marginTop: '10px', marginBottom: '5px' }}>Notifications</h5>
                            <Link to="/diagnostic/notifications" style={{ fontSize: '12px' }}>View All</Link>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Available Tests */}
            <Card
                title={<><ExperimentOutlined className="me-2" />Available Tests</>}
                className="shadow-sm border-0 mb-4"
                extra={<Link to="/diagnostic/tests">Manage Tests</Link>}
            >
                {tests.length > 0 ? (
                    <Row gutter={[16, 16]}>
                        {tests.slice(0, 8).map((test, index) => (
                            <Col xs={12} sm={8} md={6} lg={3} key={index}>
                                <Card
                                    className="text-center shadow-sm"
                                    style={{
                                        borderTop: '3px solid #1890ff'
                                    }}
                                >
                                    <ExperimentOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                                    <div style={{ fontSize: '14px', fontWeight: '500', marginTop: '8px' }}>
                                        {test.test_name}
                                    </div>
                                    <div className="text-muted" style={{ fontSize: '12px' }}>
                                        {test.category || 'General'}
                                    </div>
                                    <Tag color="green" className="mt-2">₹{test.price || 0}</Tag>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                ) : (
                    <div className="text-center text-muted py-5">
                        <ExperimentOutlined style={{ fontSize: '48px', opacity: 0.3 }} />
                        <p className="mt-3">No tests added yet. Go to Tests to add services.</p>
                    </div>
                )}
            </Card>

            {/* Appointments and Patients Tables */}
            <Row gutter={[16, 16]} className="mb-4">
                <Col xs={24} md={12}>
                    <Card
                        title={<><CalendarOutlined className="me-2" />Recent Appointments</>}
                        className="shadow-sm border-0"
                        extra={<Link to="/diagnostic/appointments">View All</Link>}
                    >
                        <Table
                            columns={appointmentColumns}
                            dataSource={appointments}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            locale={{ emptyText: 'No appointments yet' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} md={12}>
                    <Card
                        title={<><UserOutlined className="me-2" />Recent Patients</>}
                        className="shadow-sm border-0"
                        extra={<Link to="/diagnostic/patients">View All</Link>}
                    >
                        <Table
                            columns={patientColumns}
                            dataSource={patients}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            locale={{ emptyText: 'No patients yet' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Nearby Hospitals and Diagnostics */}
            <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                    <Card
                        title={<><MedicineBoxOutlined className="me-2" />Nearby Hospitals</>}
                        className="shadow-sm border-0"
                    >
                        {nearbyHospitals.length > 0 ? (
                            nearbyHospitals.map((hospital, index) => (
                                <div key={index} style={{ padding: '10px 0', borderBottom: index < nearbyHospitals.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <MedicineBoxOutlined style={{ fontSize: '24px', color: '#13c2c2', marginRight: '12px' }} />
                                        <div>
                                            <strong>{hospital.display_name || hospital.name}</strong>
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                <EnvironmentOutlined className="me-1" />
                                                {hospital.address || 'Address not available'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center text-muted py-4">No nearby hospitals found</div>
                        )}
                    </Card>
                </Col>
                <Col xs={24} md={12}>
                    <Card
                        title={<><AppstoreOutlined className="me-2" />Other Diagnostic Centers</>}
                        className="shadow-sm border-0"
                    >
                        {nearbyDiagnostics.length > 0 ? (
                            nearbyDiagnostics.map((diagnostic, index) => (
                                <div key={index} style={{ padding: '10px 0', borderBottom: index < nearbyDiagnostics.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <AppstoreOutlined style={{ fontSize: '24px', color: '#eb2f96', marginRight: '12px' }} />
                                        <div>
                                            <strong>{diagnostic.name || diagnostic.display_name}</strong>
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                <EnvironmentOutlined className="me-1" />
                                                {diagnostic.address || 'Address not available'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center text-muted py-4">No other diagnostic centers found</div>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default DiagnosticDashboard;
